import { Component, OnInit, ViewChild } from '@angular/core';
import { TitleService } from '@app/core/services/title.service';
import { SessionService } from '@app/security/session.service';
import { FeatureService } from '@app/core/services/feature.service';
import { FeatureFlags } from '@app/core/models/feature-flags';
import { MessageDialogComponent } from '@app/shared/message-dialog/message-dialog.component';
import { ApiKeyService } from '@app/api-mgmt/components/apikey-mgmt/services/api-key.service';
import {
  CreateKeyResponse,
  GetKeysResponse,
  MAX_API_KEYS,
  getKeyPrefix,
} from '@app/core/models/api-key';
import { ApikeyItemComponent } from './components/apikey-item/apikey-item.component';
import { MessageDialogComponent as MessageDialogComponent_1 } from '../../../shared/message-dialog/message-dialog.component';

@Component({
    selector: 'rn-apikey-mgmt',
    templateUrl: './apikey-mgmt.component.html',
    styleUrls: ['./apikey-mgmt.component.scss'],
    standalone: true,
    imports: [ApikeyItemComponent, MessageDialogComponent_1]
})
export class ApikeyMgmtComponent implements OnInit {
  @ViewChild('errorDialog', { static: true })
  messageDialog!: MessageDialogComponent;
  @ViewChild('confirmDialog', { static: true })
  confirmDialog!: MessageDialogComponent;
  existingKeys: string[] = [];
  clientId: string;
  canDeleteApiKey: boolean = false;
  keyPendingDelete: string | null = null;
  readonly maxApiKeys = MAX_API_KEYS;

  constructor(
    private titleService: TitleService,
    private sessionService: SessionService,
    private apiKeyService: ApiKeyService,
    private featureService: FeatureService
  ) {}

  ngOnInit() {
    this.titleService.activate('API Management');
    this.clientId = this.sessionService.getCurrentUsersClient().id;
    this.canDeleteApiKey = this.featureService.checkFlag(
      FeatureFlags.delete_apikey_ui
    );

    this.apiKeyService.getKeys(this.clientId).subscribe(
      (g: GetKeysResponse) => {
        this.existingKeys = g.apikeys;
      },
      () => {
        this.existingKeys = [];
      }
    );
  }

  hasExistingKeys(): boolean {
    return this.existingKeys && this.existingKeys.length > 0;
  }

  canCreateApiKey(): boolean {
    return !this.existingKeys || this.existingKeys.length < MAX_API_KEYS;
  }

  getKeyPrefix(key: string): string {
    return getKeyPrefix(key);
  }

  createApiKey(): void {
    this.apiKeyService.createKey(this.clientId).subscribe(
      (c: CreateKeyResponse) => {
        this.existingKeys.push(c.key);
      },
      () => {
        this.messageDialog.showMessage('Failed to create an API key.');
      }
    );
  }

  promptDeleteApiKey(key: string): void {
    this.keyPendingDelete = key;
    this.confirmDialog.showMessage();
  }

  deleteApiKey(): void {
    const key = this.keyPendingDelete;
    this.keyPendingDelete = null;
    if (!key) {
      return;
    }

    this.apiKeyService.deleteKey(this.clientId, key).subscribe(
      () => {
        this.existingKeys = this.existingKeys.filter((k) => k !== key);
      },
      () => {
        this.messageDialog.showMessage('Failed to delete the API key.');
      }
    );
  }
}
